<script setup>
import { computed } from 'vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  allItems: { type: Array, required: true },
  selectAllLabel: { type: String, default: '' },
  selectedCountLabel: { type: String, default: '' },
  deleteLabel: { type: String, default: 'Delete' },
});

const emit = defineEmits(['bulkDelete']);
const modelValue = defineModel({ type: Set, default: () => new Set() });
const selectedCount = computed(() => modelValue.value.size);
const visibleItemIds = computed(() => props.allItems.map(item => item.id));
const selectedVisibleCount = computed(
  () => visibleItemIds.value.filter(id => modelValue.value.has(id)).length
);
const allSelected = computed(
  () =>
    visibleItemIds.value.length > 0 &&
    selectedVisibleCount.value === visibleItemIds.value.length
);
const isIndeterminate = computed(
  () =>
    selectedVisibleCount.value > 0 &&
    selectedVisibleCount.value < visibleItemIds.value.length
);
const bulkCheckboxState = computed({
  get: () => allSelected.value,
  set: shouldSelectAll => {
    const updatedSelection = new Set(modelValue.value);
    visibleItemIds.value.forEach(id => {
      if (shouldSelectAll) updatedSelection.add(id);
      else updatedSelection.delete(id);
    });
    modelValue.value = updatedSelection;
  },
});
</script>

<template>
  <div
    v-if="selectedCount"
    class="flex items-center gap-3 py-1 ltr:pl-3 rtl:pr-3 ltr:pr-4 rtl:pl-4 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container shadow"
  >
    <div class="flex items-center gap-3 min-w-0">
      <div class="flex items-center gap-1.5 min-w-0">
        <Checkbox
          v-model="bulkCheckboxState"
          :indeterminate="isIndeterminate"
        />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ selectAllLabel }}
        </span>
      </div>
      <span class="text-sm text-n-slate-10 truncate tabular-nums">{{
        selectedCountLabel
      }}</span>
      <slot name="primaryActions" />
    </div>
    <div class="flex items-center gap-3 ml-auto">
      <slot name="secondaryActions" />
      <slot name="actions" :selected-count="selectedCount">
        <Button
          :label="deleteLabel"
          sm
          ruby
          ghost
          class="!px-1.5"
          icon="i-lucide-trash"
          @click="emit('bulkDelete')"
        />
      </slot>
    </div>
  </div>
  <div v-else class="flex items-center gap-3">
    <slot name="default-actions" />
  </div>
</template>
